/* eslint-disable sort-keys */
import React, { lazy } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';

import {
  LoginCallbackView,
  Login,
  LogoutView,
  ProfileView,
  RequireAuth,
  TermsAndConditions,
  WelcomeVideo,
} from 'mf_core/auth';

import { WhiteList } from './WhiteList';
import { BlackList } from './BlackList';

const FrameView = lazy(
  () =>
    import(/* webpackChunkName: "FrameView" */ '../../views/frame/FrameView')
);

const AppCounterView = lazy(
  () =>
    import(
      /* webpackChunkName: "AppCounterView" */ '../../views/test/AppCounterView'
    )
);
const DebugInfoView = lazy(
  () =>
    import(
      /* webpackChunkName: "DebugInfoView" */ '../../views/test/DebugInfoView'
    )
);
const IframeTestView = lazy(
  () =>
    import(
      /* webpackChunkName: "IframeTestView" */ '../../views/test/IframeTestView'
    )
);
const NoHeaderView = lazy(
  () =>
    import(
      /* webpackChunkName: "NoHeaderView" */ '../../views/test/NoHeaderView'
    )
);
const RemoteDialogTestView = lazy(
  () =>
    import(
      /* webpackChunkName: "RemoteDialogTestView" */ '../../views/test/RemoteDialogTestView'
    )
);
const PostMsgTestView = lazy(
  () =>
    import(
      /* webpackChunkName: "PostMsgTestView" */ '../../views/test/PostMsgTestView'
    )
);
const NoMatchView = lazy(
  () => import(/* webpackChunkName: "NoMatchView" */ '../../views/NoMatchView')
);
const DevToolView = lazy(
  () =>
    import(
      /* webpackChunkName: "DeveloperTools" */ '../../views/DeveloperTools'
    )
);

/**
 * url search params whitelist for `/frame-public` route
 * Note: whitelist uses lowercase url param
 * the param is converted to lowercase before comparing
 * */
const publicUrlWhiteList = ['/site/password/forgot'];
const imonlineTermsAndConditionsUrl = ['/site/login/termsandconditions'];

export function buildAppRoutes(
  authRoutes: RouteObject[],
  publicRoutes: RouteObject[]
): RouteObject[] {
  return [
    ...publicRoutes,
    {
      path: 'my',
      element: <RequireAuth children={<Outlet />} />,
      children: [
        ...authRoutes,
        {
          path: 'profile',
          element: <ProfileView />,
        },
      ],
    },
    {
      path: 'login',
      element: <Login />,
    },
    {
      path: 'login/callback',
      element: <LoginCallbackView />,
    },
    {
      path: 'logout',
      element: <LogoutView />,
    },
    {
      path: 'welcome-video',
      element: <RequireAuth children={<WelcomeVideo />} />,
    },
    {
      path: 'terms-and-conditions',
      element: <RequireAuth children={<TermsAndConditions />} />,
    },
    // Test Views
    {
      path: 'debug-info',
      element: <DebugInfoView />,
    },
    {
      path: 'iframe-test',
      element: <IframeTestView />,
    },
    {
      path: 'test-hh',
      element: <NoHeaderView />,
    },
    {
      path: 'state-test',
      element: <AppCounterView />,
    },
    {
      path: 'post-msg-test',
      element: <PostMsgTestView />,
    },
    {
      path: 'remote-dialog-test',
      element: <RemoteDialogTestView />,
    },
    // Other Views
    {
      path: 'frame',
      element: (
        <BlackList
          children={<RequireAuth children={<FrameView />} />}
          urlList={imonlineTermsAndConditionsUrl}
        />
      ),
    },
    {
      path: 'frame-public',
      element: (
        <WhiteList children={<FrameView />} urlList={publicUrlWhiteList} />
      ),
    },
    {
      path: 'index',
      element: <Navigate replace to="/home" />,
    },
    {
      path: 'index.html',
      element: <Navigate replace to="/home" />,
    },
    {
      path: '/home',
      element: <Navigate replace to="/home" />,
    },
    {
      path: '/',
      element: <Navigate replace to="/home" />,
    },
    {
      path: '*',
      element: <NoMatchView />,
    },
    {
      path: '/developer-tools',
      element: <DevToolView />,
    },
  ];
}
